import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  PlayIcon,
  PauseIcon,
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { useGetLesson, useGetLessonContainedTopics } from "../../api/queries";
import { useUpdateLesson } from "../../api/mutations";
import { ComponentEnum, LessonI } from "../../types";
import { useNotification } from "../../hooks/useNotification";
import Container from "../../componentLibrary/Container";
import Button from "../../componentLibrary/Button";
import Spinner from "../../componentLibrary/Spinner";
import ConfirmationBox from "../../componentLibrary/ConfirmationBox";
import ExternalTextEditor from "../../componentLibrary/ExternalTextEditor";

interface TranscriptSegment {
  pageTitle: string;
  transcript: string;
  pageIndex: number;
}

const TeleprompterPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { data: lesson, isLoading, isError } = useGetLesson(lessonId);
  const { data: lessonContainedModules = [] } =
    useGetLessonContainedTopics(lessonId);

  // Get moduleId from navigation state (if coming from module page) or URL params
  const moduleId =
    location.state?.moduleId ||
    new URLSearchParams(location.search).get("moduleId");

  const mutation = useUpdateLesson({
    onSuccess: async () => {
      notify("success", "Transcript updated successfully");
      setHasChanges(false);
      await queryClient.invalidateQueries({ queryKey: ["lessons"] });
    },
    onError: async () => {
      notify("error", "There was an error updating the transcript.");
    },
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [fontSize, setFontSize] = useState<
    "small" | "medium" | "large" | "extra-large"
  >("medium");
  const [lineSpacing, setLineSpacing] = useState<
    "normal" | "loose" | "extra-loose"
  >("normal");
  const [transcripts, setTranscripts] = useState<TranscriptSegment[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedLesson, setEditedLesson] = useState<LessonI | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] =
    useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<number | null>(null);

  // Speed calculation function
  const getSpeed = (
    speedType: "slowest" | "slow" | "normal" | "fast" | "extra-fast",
  ) => {
    switch (speedType) {
      case "slowest":
        return 0.2;
      case "slow":
        return 0.5;
      case "normal":
        return 1.0;
      case "fast":
        return 2.0;
      case "extra-fast":
        return 5.0;
      default:
        return 1.0;
    }
  };

  // Font size calculation function
  const getFontSize = (size: "small" | "medium" | "large" | "extra-large") => {
    switch (size) {
      case "small":
        return "text-lg";
      case "medium":
        return "text-xl";
      case "large":
        return "text-2xl";
      case "extra-large":
        return "text-3xl";
      default:
        return "text-xl";
    }
  };

  // Line spacing calculation function
  const getLineSpacing = (spacing: "normal" | "loose" | "extra-loose") => {
    switch (spacing) {
      case "normal":
        return "leading-relaxed";
      case "loose":
        return "leading-loose";
      case "extra-loose":
        return "leading-[2.5]";
      default:
        return "leading-relaxed";
    }
  };

  // Extract transcripts from lesson pages
  useEffect(() => {
    if (lesson) {
      const extractedTranscripts: TranscriptSegment[] = [];

      lesson.pages.forEach((page, pageIndex) => {
        page.components.forEach((component) => {
          if (
            component.type === ComponentEnum.VIDEO &&
            component.content.transcript
          ) {
            extractedTranscripts.push({
              pageTitle: page.title,
              transcript: component.content.transcript,
              pageIndex,
            });
          }
        });
      });

      setTranscripts(extractedTranscripts);
      setEditedLesson(lesson);
    }
  }, [lesson]);

  // Auto-scroll functionality
  useEffect(() => {
    if (isPlaying && transcripts.length > 0 && !isEditMode) {
      let lastTime = performance.now();
      let accumulatedScroll = 0;

      const animateScroll = (currentTime: number) => {
        if (!isPlaying || !scrollRef.current || isEditMode) return;

        const deltaTime = currentTime - lastTime;
        const scrollSpeed = 30 * speed; // pixels per second, multiplied by speed
        const scrollDistance = (scrollSpeed * deltaTime) / 1000; // Convert to pixels per frame

        accumulatedScroll += scrollDistance;

        if (scrollRef.current && accumulatedScroll >= 1) {
          const scrollAmount = Math.floor(accumulatedScroll);
          scrollRef.current.scrollTop += scrollAmount;
          accumulatedScroll -= scrollAmount;

          // Check if we've reached the bottom
          const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
          if (scrollTop + clientHeight >= scrollHeight) {
            setIsPlaying(false);
            return;
          }
        }

        lastTime = currentTime;
        intervalRef.current = requestAnimationFrame(animateScroll);
      };

      intervalRef.current = requestAnimationFrame(animateScroll);
    } else {
      if (intervalRef.current) {
        cancelAnimationFrame(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        cancelAnimationFrame(intervalRef.current);
      }
    };
  }, [isPlaying, speed, isEditMode]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSpeedChange = (
    speedType: "slowest" | "slow" | "normal" | "fast" | "extra-fast",
  ) => {
    setSpeed(getSpeed(speedType));
  };

  const handleFontSizeChange = (
    size: "small" | "medium" | "large" | "extra-large",
  ) => {
    setFontSize(size);
  };

  const handleLineSpacingChange = (
    spacing: "normal" | "loose" | "extra-loose",
  ) => {
    setLineSpacing(spacing);
  };

  const handleEditModeToggle = () => {
    if (isEditMode && hasChanges) {
      // Save changes before exiting edit mode
      handleSaveChanges();
    }
    setIsEditMode(!isEditMode);
  };

  const handleSaveChanges = () => {
    if (!editedLesson) return;

    mutation.mutate({
      lesson: editedLesson,
      modules: lessonContainedModules.map((module) => module._id),
    });
  };

  const handleCancelChanges = () => {
    if (hasChanges) {
      setIsCancelConfirmationOpen(true);
    } else {
      setEditedLesson(lesson);
      setHasChanges(false);
      setIsEditMode(false);
    }
  };

  const handleDiscardChanges = () => {
    // Reload the lesson data from the database
    queryClient.invalidateQueries({ queryKey: ["lessons", lessonId] });
    setHasChanges(false);
    setIsEditMode(false);
    setIsCancelConfirmationOpen(false);
  };

  const handleTranscriptChange = (
    pageIndex: number,
    componentIndex: number,
    newTranscript: string,
  ) => {
    if (!editedLesson) return;

    setHasChanges(true);
    setEditedLesson((prev) => {
      if (!prev) return prev;

      const newLesson = { ...prev };
      const page = newLesson.pages[pageIndex];

      if (page && page.components[componentIndex]) {
        page.components[componentIndex] = {
          ...page.components[componentIndex],
          content: {
            ...page.components[componentIndex].content,
            transcript: newTranscript,
          },
        };
      }

      return newLesson;
    });
  };

  const handleSegmentClick = (index: number) => {
    // Scroll to the clicked segment
    const segmentElement = document.getElementById(`segment-${index}`);
    if (segmentElement && scrollRef.current) {
      const container = scrollRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = segmentElement.getBoundingClientRect();
      const targetScrollTop =
        container.scrollTop +
        elementRect.top -
        containerRect.top -
        containerRect.height / 2 +
        elementRect.height / 2;
      container.scrollTop = targetScrollTop;
    }
  };

  const handleBack = () => {
    // If we have a moduleId, navigate back to the content management page with that module selected
    if (moduleId) {
      navigate("/admin/content-management", { state: { moduleId } });
    } else {
      // Fallback to browser back
      navigate(-1);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return; // Don't handle shortcuts when typing in inputs
      }

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          if (isEditMode) {
            handleCancelChanges();
          } else {
            handleEditModeToggle();
          }
          break;
        case "s":
        case "S":
          // Ctrl/Cmd+S to save in edit mode
          if (isEditMode && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            handleSaveChanges();
          }
          break;
        case " ":
          // Disable keyboard shortcuts in edit mode (except escape and save)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          handlePlayPause();
          break;
        case "x":
        case "X":
          // Disable keyboard shortcuts in edit mode (except escape)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          if (isPlaying) {
            handlePlayPause();
          }
          break;
        case "1":
          // Disable keyboard shortcuts in edit mode (except escape)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          handleSpeedChange("slowest");
          break;
        case "2":
          // Disable keyboard shortcuts in edit mode (except escape)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          handleSpeedChange("slow");
          break;
        case "3":
          // Disable keyboard shortcuts in edit mode (except escape)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          handleSpeedChange("normal");
          break;
        case "4":
          // Disable keyboard shortcuts in edit mode (except escape)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          handleSpeedChange("fast");
          break;
        case "5":
          // Disable keyboard shortcuts in edit mode (except escape)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          handleSpeedChange("extra-fast");
          break;
        case "ArrowLeft":
          // Disable keyboard shortcuts in edit mode (except escape)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          if (scrollRef.current) {
            scrollRef.current.scrollTop -= 100;
          }
          break;
        case "ArrowRight":
          // Disable keyboard shortcuts in edit mode (except escape)
          if (isEditMode) {
            return;
          }
          event.preventDefault();
          if (scrollRef.current) {
            scrollRef.current.scrollTop += 100;
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [transcripts.length, navigate, isEditMode, isPlaying]);

  if (isLoading) return <Spinner />;
  if (isError || !lesson) return <div>Error loading lesson</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Container className="max-w-4xl mx-auto py-4">
        {/* Transcripts - Moved to top */}
        <div
          ref={scrollRef}
          className="space-y-8 max-h-[60vh] overflow-y-auto scroll-smooth mb-8"
        >
          {transcripts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-xl">
                No transcripts found in this lesson.
              </p>
              <p className="text-gray-400 text-base mt-2">
                Transcripts are available for video components with transcript
                content.
              </p>
            </div>
          ) : (
            transcripts.map((segment, index) => {
              // Find the corresponding component in the edited lesson
              const editedTranscript =
                editedLesson?.pages[segment.pageIndex]?.components.find(
                  (component) =>
                    component.type === ComponentEnum.VIDEO &&
                    component.content.transcript,
                )?.content.transcript || segment.transcript;

              // Find the component index for this transcript
              const componentIndex =
                editedLesson?.pages[segment.pageIndex]?.components.findIndex(
                  (component) =>
                    component.type === ComponentEnum.VIDEO &&
                    component.content.transcript,
                ) || 0;

              return (
                <div
                  key={index}
                  id={`segment-${index}`}
                  className={`p-8 rounded-lg border-2 border-gray-200 bg-white transition-all duration-500 ${
                    isEditMode
                      ? "border-blue-300"
                      : "hover:border-gray-300 cursor-pointer"
                  }`}
                  onClick={
                    !isEditMode ? () => handleSegmentClick(index) : undefined
                  }
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-semibold text-gray-900">
                      {segment.pageTitle}
                    </h3>
                    <span className="text-lg text-gray-500">
                      Page {segment.pageIndex + 1}
                    </span>
                  </div>
                  {isEditMode ? (
                    <div className="mt-4">
                      <ExternalTextEditor
                        value={editedTranscript}
                        handleChange={(value) =>
                          handleTranscriptChange(
                            segment.pageIndex,
                            componentIndex,
                            value,
                          )
                        }
                        placeholder="Edit transcript content..."
                      />
                    </div>
                  ) : (
                    <div
                      className={`prose max-w-none text-gray-700 ${getFontSize(fontSize)} ${getLineSpacing(lineSpacing)}`}
                      dangerouslySetInnerHTML={{ __html: editedTranscript }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Controls - Moved up right after transcripts */}
        {transcripts.length > 0 && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-4">
              <div className="flex items-center justify-between w-full flex-wrap gap-8">
                <div className="flex flex-col items-center space-y-1">
                  <Button.Square
                    onClick={isEditMode ? handleCancelChanges : handlePlayPause}
                    disabled={transcripts.length === 0}
                    variant={isEditMode ? "secondary" : "dark"}
                    className="w-8 h-8"
                    title={isEditMode ? "Cancel Edit" : "Play/Pause"}
                  >
                    {isEditMode ? (
                      <XMarkIcon className="w-4 h-4" />
                    ) : isPlaying ? (
                      <PauseIcon className="w-4 h-4" />
                    ) : (
                      <PlayIcon className="w-4 h-4" />
                    )}
                  </Button.Square>

                  {transcripts.length > 0 && (
                    <>
                      {isEditMode ? (
                        <Button.Square
                          variant="primary"
                          onClick={handleSaveChanges}
                          disabled={!hasChanges || mutation.isPending}
                          title="Save Changes"
                          className="w-8 h-8"
                        >
                          <CheckIcon className="w-4 h-4" />
                        </Button.Square>
                      ) : (
                        <Button.Square
                          variant="secondary"
                          onClick={handleEditModeToggle}
                          title="Edit Transcript"
                          className="w-8 h-8"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Button.Square>
                      )}
                    </>
                  )}
                </div>

                <div className="flex flex-col items-center space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">
                      Speed
                    </span>
                    <span className="text-sm text-gray-600">
                      {speed.toFixed(1)}x
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button.Square
                      variant={speed === 0.2 ? "primary" : "secondary"}
                      onClick={() => handleSpeedChange("slowest")}
                      title="Slowest Speed"
                      className="w-8 h-8"
                    >
                      XS
                    </Button.Square>
                    <Button.Square
                      variant={speed === 0.5 ? "primary" : "secondary"}
                      onClick={() => handleSpeedChange("slow")}
                      title="Slow Speed"
                      className="w-8 h-8"
                    >
                      S
                    </Button.Square>
                    <Button.Square
                      variant={speed === 1.0 ? "primary" : "secondary"}
                      onClick={() => handleSpeedChange("normal")}
                      title="Normal Speed"
                      className="w-8 h-8"
                    >
                      M
                    </Button.Square>
                    <Button.Square
                      variant={speed === 2.0 ? "primary" : "secondary"}
                      onClick={() => handleSpeedChange("fast")}
                      title="Fast Speed"
                      className="w-8 h-8"
                    >
                      F
                    </Button.Square>
                    <Button.Square
                      variant={speed === 5.0 ? "primary" : "secondary"}
                      onClick={() => handleSpeedChange("extra-fast")}
                      title="Extra Fast Speed"
                      className="w-8 h-8"
                    >
                      XF
                    </Button.Square>
                  </div>
                </div>

                <div className="flex flex-col items-center space-y-2">
                  <span className="text-sm font-medium text-gray-700">
                    Font
                  </span>
                  <div className="flex items-center space-x-2">
                    <Button.Square
                      variant={fontSize === "small" ? "primary" : "secondary"}
                      onClick={() => handleFontSizeChange("small")}
                      title="Small Font"
                      className="w-8 h-8"
                    >
                      S
                    </Button.Square>
                    <Button.Square
                      variant={fontSize === "medium" ? "primary" : "secondary"}
                      onClick={() => handleFontSizeChange("medium")}
                      title="Medium Font"
                      className="w-8 h-8"
                    >
                      M
                    </Button.Square>
                    <Button.Square
                      variant={fontSize === "large" ? "primary" : "secondary"}
                      onClick={() => handleFontSizeChange("large")}
                      title="Large Font"
                      className="w-8 h-8"
                    >
                      L
                    </Button.Square>
                    <Button.Square
                      variant={
                        fontSize === "extra-large" ? "primary" : "secondary"
                      }
                      onClick={() => handleFontSizeChange("extra-large")}
                      title="Extra Large Font"
                      className="w-8 h-8"
                    >
                      XL
                    </Button.Square>
                  </div>
                </div>

                <div className="flex flex-col items-center space-y-2">
                  <span className="text-sm font-medium text-gray-700">
                    Spacing
                  </span>
                  <div className="flex items-center space-x-2">
                    <Button.Square
                      variant={
                        lineSpacing === "normal" ? "primary" : "secondary"
                      }
                      onClick={() => handleLineSpacingChange("normal")}
                      title="Normal Spacing"
                      className="w-8 h-8"
                    >
                      1
                    </Button.Square>
                    <Button.Square
                      variant={
                        lineSpacing === "loose" ? "primary" : "secondary"
                      }
                      onClick={() => handleLineSpacingChange("loose")}
                      title="Loose Spacing"
                      className="w-8 h-8"
                    >
                      2
                    </Button.Square>
                    <Button.Square
                      variant={
                        lineSpacing === "extra-loose" ? "primary" : "secondary"
                      }
                      onClick={() => handleLineSpacingChange("extra-loose")}
                      title="Extra Loose Spacing"
                      className="w-8 h-8"
                    >
                      3
                    </Button.Square>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Back button and page selection at the bottom */}
        <div className="flex justify-center items-center space-x-6 mt-8">
          <Button variant="alternative" size="sm" onClick={handleBack}>
            <ArrowLeftIcon className="w-3 h-3 mr-1" />
            Back to Content Manager
          </Button>
          {transcripts.length > 0 && (
            <div className="flex items-center space-x-1">
              <span className="text-sm text-gray-600">Page:</span>
              <select
                className="text-xs px-2 py-1 border border-gray-300 rounded bg-white"
                onChange={(e) => {
                  const index = parseInt(e.target.value);
                  if (!isNaN(index) && scrollRef.current) {
                    const segmentElement = document.getElementById(
                      `segment-${index}`,
                    );
                    if (segmentElement) {
                      const container = scrollRef.current;
                      const containerRect = container.getBoundingClientRect();
                      const elementRect =
                        segmentElement.getBoundingClientRect();
                      const targetScrollTop =
                        container.scrollTop +
                        elementRect.top -
                        containerRect.top -
                        containerRect.height / 2 +
                        elementRect.height / 2;
                      container.scrollTop = targetScrollTop;
                    }
                  }
                }}
              >
                {transcripts.map((segment, index) => (
                  <option key={index} value={index}>
                    {segment.pageTitle}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </Container>
      <ConfirmationBox
        isOpen={isCancelConfirmationOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to save them before switching to read mode?"
        confirmText="Save & Read"
        cancelText="Discard Changes"
        onConfirm={() => {
          handleSaveChanges();
          setIsEditMode(false);
          setIsCancelConfirmationOpen(false);
        }}
        onCancel={handleDiscardChanges}
        onCloseBox={() => setIsCancelConfirmationOpen(false)}
      />
    </div>
  );
};

export default TeleprompterPage;
